import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { StarIconFull, StarIconHalfFull } from '@icons/StarIcon';
import { calculateStartCount, formatRatingCount } from '@insights_module/utils/showsUtils';
import { useSelectedShowDetails } from '@insights_module/hooks/useGraphqlShowDetails';

const ShowInfoWrapper = tw.div`flex flex-col gap-[12px] items-start w-full h-full`;
const ShowInfoTitle = tw.div`text-[16px] font-semibold text-text-and-icon`;
const ShowInfoContentWrapper = tw.div`flex flex-col items-start rounded-[8px] border border-gray-1 w-full overflow-hidden`;
const Divider = tw.div`h-[1px] w-full bg-gray-1`;
const RatingWrapper = tw.div`flex flex-col p-[12px] justify-between items-stretch gap-[8px] w-full`;
const RatingInnerWrapper = tw.div`flex gap-[20px] justify-between items-center`;
const RatingInfo = tw.span`text-lg font-bold text-text-and-icon`;
const DataInfo = tw.span`text-sm font-medium text-text-and-icon capitalize`;
const DataContainer = tw.div`flex gap-[12px] items-start justify-start w-full p-[12px]`;
const DataTitle = tw.span`text-sm text-text-and-icon-muted-2`;
const DescriptionWrapper = tw.div`flex flex-col relative w-full h-full p-[12px] overflow-hidden`;
const DescriptionTitle = tw.span`text-text-and-icon-muted-2 text-sm font-semibold`;
const DescriptionContent = tw.div`w-full h-full text-text-and-icon-muted-2 whitespace-pre-wrap [overflow-wrap: break-word] overflow-scroll`;
const CategoryInfo = tw.span`flex py-[4px] px-[12px] max-h-[30px] bg-sec-4 border border-sec-3 rounded-full text-sm font-medium text-text-and-icon`;

export const ShowMetadataOverview = () => {
  const { t } = useTranslation();
  const { data: show } = useSelectedShowDetails();

  if (!show) {
    return null;
  }

  const { podcast_metadata: metadata } = show;
  const { fullStarArray, shouldShowHalfStar, emptyStarsArray } = calculateStartCount(
    metadata?.apple_star_rating || 0
  );

  return (
    <ShowInfoWrapper>
      <ShowInfoTitle>{t('insights.show_detail_sidebar.info.title')}</ShowInfoTitle>
      <ShowInfoContentWrapper>
        <RatingWrapper>
          <RatingInnerWrapper>
            <RatingInfo title='Apple review rating'>{`${
              metadata?.apple_star_rating || '-'
            }/5`}</RatingInfo>
            <span tw='flex gap-[2px]'>
              {fullStarArray.map((_, index) => (
                <StarIconFull tw='fill-accent-2 stroke-accent-2' key={index} />
              ))}
              {shouldShowHalfStar && <StarIconHalfFull />}
              {emptyStarsArray.map((_, index) => (
                <StarIconFull tw='fill-white stroke-accent-2' key={index} />
              ))}
            </span>
          </RatingInnerWrapper>
          <RatingInnerWrapper>
            <DataInfo title='Apple review count'>{`(${formatRatingCount(
              metadata?.apple_rating_count
            )})`}</DataInfo>
            <DataTitle>{t('insights.show_details_drawer.metadata.apple_rating')}</DataTitle>
          </RatingInnerWrapper>
        </RatingWrapper>

        <Divider />

        <DataContainer>
          <DataTitle>{t('insights.show_details_drawer.metadata.author')}:</DataTitle>
          <DataInfo>{metadata?.author || '-'}</DataInfo>
        </DataContainer>

        <DataContainer>
          <DataTitle>{t('insights.show_details_drawer.metadata.type')}:</DataTitle>
          <DataInfo>{metadata?.podcast_type || '-'}</DataInfo>
        </DataContainer>

        <DataContainer tw='flex-col'>
          <DataTitle>{t('insights.show_details_drawer.metadata.categories')}:</DataTitle>
          <div tw='flex gap-[4px] flex-wrap'>
            {metadata?.apple_categories?.map((category, index) => (
              <CategoryInfo key={index}>{category}</CategoryInfo>
            ))}
          </div>
        </DataContainer>

        <Divider />

        <DescriptionWrapper>
          <DescriptionTitle>
            {t('insights.show_details_drawer.metadata.description')}:
          </DescriptionTitle>
          <DescriptionContent>{metadata?.description}</DescriptionContent>
        </DescriptionWrapper>
      </ShowInfoContentWrapper>
    </ShowInfoWrapper>
  );
};
